import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useGpsPings, useMCPTT, useNetworks, useTalkgroupActivity } from "../mcptt/hooks";
import { StatusBar } from "./StatusBar";
import { AppBar } from "./AppBar";
import { EmergencyBanner } from "./EmergencyBanner";
import { SubscribersPanel } from "./SubscribersPanel";
import { TalkgroupsPanel } from "./TalkgroupsPanel";
import { MapPanel } from "./MapPanel";
import { EventLogPanel } from "./EventLogPanel";
import { SubscriberDetail } from "./SubscriberDetail";
import { GpsPingsDrawer } from "./GpsPingsDrawer";
import { DiagnosticsPanel } from "./DiagnosticsPanel";

/**
 * Post-login console shell.
 *
 * Holds cross-panel UI state:
 *   - selectedTalkgroupId  : highlighted tile + appbar mini-card + spacebar TX target
 *   - selectedSubscriberId : highlighted row + opens detail flyout over map
 *   - focusedDeviceId      : map pans + opens InfoWindow (auto-clears after 12s)
 *   - isVisualTx           : space-held visual transmit indicator (no real PTT
 *                            until Phase 3 audio lands)
 */
export function Shell() {
  const { snapshot, client } = useMCPTT();
  const networks = useNetworks();

  const [networkId, setNetworkId] = useState<string | null>(null);
  useEffect(() => {
    if (!networkId && networks.length > 0) setNetworkId(networks[0].ID);
  }, [networks, networkId]);

  const [selectedTgId,    setSelectedTgId]    = useState<string | null>(null);
  const [selectedSubId,   setSelectedSubId]   = useState<string | null>(null);
  const [focusedDeviceId, setFocusedDeviceId] = useState<string | null>(null);
  const [focusBump, setFocusBump]             = useState(0);
  const [activePttGroupId, setActivePttGroupId] = useState<string | null>(null);
  const [gpsDrawerOpen, setGpsDrawerOpen]     = useState(false);
  const [diagOpen, setDiagOpen]               = useState(false);
  const [mutedGroupIds, setMutedGroupIds]     = useState<Set<string>>(() => new Set());
  const [pinnedGroupIds, setPinnedGroupIds]   = useState<Set<string>>(() => new Set());

  // Reset selections whenever the active network changes.
  useEffect(() => {
    setSelectedTgId(null);
    setSelectedSubId(null);
    setFocusedDeviceId(null);
    setActivePttGroupId(null);
    setGpsDrawerOpen(false);
    setMutedGroupIds(new Set());
  }, [networkId]);

  const toggleGroupMute = useCallback((groupId: string) => {
    setMutedGroupIds((prev) => {
      const next = new Set(prev);
      const muted = !next.has(groupId);
      if (muted) next.add(groupId);
      else next.delete(groupId);
      client.setTargetMuted(groupId, muted);
      return next;
    });
  }, [client]);

  const toggleGroupPin = useCallback((groupId: string) => {
    setPinnedGroupIds((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  }, []);

  // Ctrl+Shift+D toggles the diagnostics overlay anywhere in the app.
  // Captured at the document level so it works even when an input is
  // focused. Uses code so it's keyboard-layout agnostic.
  useEffect(() => {
    function onDown(e: KeyboardEvent) {
      if (e.code !== "KeyD") return;
      if (!e.ctrlKey || !e.shiftKey) return;
      e.preventDefault();
      setDiagOpen((v) => !v);
    }
    document.addEventListener("keydown", onDown);
    return () => document.removeEventListener("keydown", onDown);
  }, []);

  // Event log "REPLY" button selects the target talkgroup so Space
  // or the card hold-to-talk immediately replies on that channel.
  useEffect(() => {
    function onSelect(e: Event) {
      const detail = (e as CustomEvent<{ targetId?: string; channel?: string; callId?: string }>).detail;
      if (detail?.targetId) {
        console.info("[mcptt] Reply armed in Shell", detail);
        setSelectedTgId(detail.targetId);
      }
    }
    window.addEventListener("mcptt:select-talkgroup", onSelect);
    return () => window.removeEventListener("mcptt:select-talkgroup", onSelect);
  }, []);

  // Auto-clear focus after a beat so the operator's next interaction
  // (panning, zooming) doesn't snap them back.
  const focusClearRef = useRef<number | null>(null);
  useEffect(() => {
    if (!focusedDeviceId) return;
    if (focusClearRef.current) window.clearTimeout(focusClearRef.current);
    focusClearRef.current = window.setTimeout(() => setFocusedDeviceId(null), 12_000);
    return () => {
      if (focusClearRef.current) window.clearTimeout(focusClearRef.current);
    };
  }, [focusedDeviceId, focusBump]);

  const focusOnDevice = useCallback((deviceId: string) => {
    setFocusedDeviceId(deviceId);
    setFocusBump((n) => n + 1);
  }, []);

  // SOS click on the banner: focus the map + flash-select the unit in
  // the subscriber list.
  const focusOnEmergency = useCallback(
    (userId: string, deviceId: string) => {
      setSelectedSubId(userId);
      focusOnDevice(deviceId);
    },
    [focusOnDevice],
  );

  // Spacebar = TX hold. Dual-mode:
  //   - Audio ON  → fires real client.startPTT / stopPTT — opens the
  //                 mic, frames go on the wire as VOICE_PACKETs.
  //   - Audio OFF → visual indicator only; no PTT_REQUEST sent. This
  //                 prevents creating phantom (silent) calls on the
  //                 manager when the operator hasn't enabled audio.
  //
  // The TX target is captured once on key-down so a network/channel
  // swap mid-press still releases the originally-armed channel.
  const txTargetRef = useRef<{ networkId: string; tgId: string } | null>(null);
  const audioReady = snapshot.audioState === "ready";
  const startPtt = useCallback((tgId: string) => {
    if (!networkId) return;
    if (txTargetRef.current) return;
    setSelectedTgId(tgId);
    txTargetRef.current = { networkId, tgId };
    setActivePttGroupId(tgId);
    if (audioReady) {
      try {
        client.startPTT({ networkId, destinationId: tgId });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[mcptt] startPTT failed", err);
      }
    }
  }, [audioReady, client, networkId]);

  const stopPtt = useCallback((tgId?: string) => {
    const target = txTargetRef.current;
    if (!target) return;
    if (tgId && target.tgId !== tgId) return;
    txTargetRef.current = null;
    setActivePttGroupId(null);
    if (audioReady) {
      try {
        client.stopPTT({ networkId: target.networkId, destinationId: target.tgId });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[mcptt] stopPTT failed", err);
      }
    }
  }, [audioReady, client]);

  useEffect(() => {
    function shouldIgnore(e: KeyboardEvent): boolean {
      const t = e.target as HTMLElement | null;
      if (!t) return false;
      const tag = t.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
      if (t.isContentEditable) return true;
      return false;
    }
    function onDown(e: KeyboardEvent) {
      if (e.code !== "Space" || e.repeat) return;
      if (shouldIgnore(e)) return;
      if (!selectedTgId || !networkId) return;
      e.preventDefault();
      startPtt(selectedTgId);
    }
    function onUp(e: KeyboardEvent) {
      if (e.code !== "Space") return;
      if (shouldIgnore(e)) return;
      e.preventDefault();
      stopPtt();
    }
    function onBlur() {
      // Window lost focus → safety release. Otherwise the user can
      // alt-tab while holding space and come back to a stuck TX.
      stopPtt();
    }
    document.addEventListener("keydown", onDown);
    document.addEventListener("keyup",   onUp);
    window.addEventListener("blur", onBlur);
    return () => {
      document.removeEventListener("keydown", onDown);
      document.removeEventListener("keyup",   onUp);
      window.removeEventListener("blur", onBlur);
    };
  }, [selectedTgId, networkId, startPtt, stopPtt]);

  const network = useMemo(
    () => networks.find((n) => n.ID === networkId),
    [networks, networkId],
  );
  const tgActivity = useTalkgroupActivity(networkId);
  const selectedTg = useMemo(
    () => (network?.groups ?? []).find((g) => g.ID === selectedTgId),
    [network, selectedTgId],
  );
  const selectedTgState = selectedTgId ? tgActivity.get(selectedTgId) : undefined;

  const keyboardOrderedGroups = useMemo(() => {
    const groups = [...(network?.groups ?? [])];
    groups.sort((a, b) => {
      const ap = pinnedGroupIds.has(a.ID) ? 0 : 1;
      const bp = pinnedGroupIds.has(b.ID) ? 0 : 1;
      if (ap !== bp) return ap - bp;
      const pa = a.Priority ?? 0;
      const pb = b.Priority ?? 0;
      if (pa !== pb) return pb - pa;
      return a.Name.localeCompare(b.Name);
    });
    return groups;
  }, [network?.groups, pinnedGroupIds]);

  // Operator keyboard map:
  //   1-9 select top visible/pinned talkgroups
  //   M   mute/unmute selected talkgroup
  //   P   pin/unpin selected talkgroup
  useEffect(() => {
    function shouldIgnore(e: KeyboardEvent): boolean {
      const t = e.target as HTMLElement | null;
      if (!t) return false;
      const tag = t.tagName;
      return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || t.isContentEditable;
    }
    function onDown(e: KeyboardEvent) {
      if (shouldIgnore(e)) return;
      if (/^Digit[1-9]$/.test(e.code)) {
        const idx = Number(e.code.slice(5)) - 1;
        const g = keyboardOrderedGroups[idx];
        if (g) {
          e.preventDefault();
          setSelectedTgId(g.ID);
        }
        return;
      }
      if (e.code === "KeyM" && selectedTgId) {
        e.preventDefault();
        toggleGroupMute(selectedTgId);
        return;
      }
      if (e.code === "KeyP" && selectedTgId) {
        e.preventDefault();
        toggleGroupPin(selectedTgId);
      }
    }
    document.addEventListener("keydown", onDown);
    return () => document.removeEventListener("keydown", onDown);
  }, [keyboardOrderedGroups, selectedTgId, toggleGroupMute, toggleGroupPin]);

  // GPS pings: one ring buffer driven by the client's device:gps event.
  // Auto-open the drawer the FIRST time a ping arrives while it's
  // closed; once the operator interacts (open/close), respect their
  // choice. We track that by snapshotting the count we've already
  // shown for so the same auto-open doesn't re-fire on each render.
  const gpsPings = useGpsPings(networkId);
  const freshPingIds = useMemo(() => {
    const s = new Set<string>();
    for (const p of gpsPings) if (p.isFresh) s.add(p.deviceId);
    return s;
  }, [gpsPings]);
  const lastSeenPingCountRef = useRef(0);
  useEffect(() => {
    if (gpsPings.length > lastSeenPingCountRef.current && !gpsDrawerOpen) {
      // First arrival in a closed drawer → pop it open briefly.
      setGpsDrawerOpen(true);
      const timer = window.setTimeout(() => {
        // If the operator hasn't kept it open by clicking inside, fade
        // it back. Heuristic: still no fresh-marker hover, still open.
        setGpsDrawerOpen((cur) => cur);   // no-op placeholder; we leave it open and let the operator close. Cheaper and less surprising than auto-collapse.
      }, 5_000);
      return () => window.clearTimeout(timer);
    }
    lastSeenPingCountRef.current = gpsPings.length;
  }, [gpsPings.length, gpsDrawerOpen]);

  return (
    <div className="app-shell">
      <AppBar
        networks={networks}
        currentNetworkId={networkId}
        onSelectNetwork={setNetworkId}
        selectedTgName={selectedTg?.Name ?? null}
        selectedTgState={selectedTgState?.state ?? "idle"}
        selectedTgLastTalker={selectedTgState?.lastTalker}
        onClearSelection={() => setSelectedTgId(null)}
        isVisualTx={activePttGroupId !== null}
        diagOpen={diagOpen}
        onToggleDiag={() => setDiagOpen((v) => !v)}
      />
      {snapshot.emergencies.size > 0 ? (
        <EmergencyBanner onFocusEmergency={focusOnEmergency} />
      ) : null}

      <div className="app-shell-body">
        {/* LEFT — Subscribers, full height */}
        <div style={{ gridRow: "1 / span 2", minHeight: 0 }}>
          <SubscribersPanel
            networkId={networkId}
            selectedId={selectedSubId}
            onSelect={(id) => setSelectedSubId((cur) => (cur === id ? null : id))}
          />
        </div>

        {/* TOP-CENTER — Talkgroups */}
        <div style={{ minHeight: 0 }}>
          <TalkgroupsPanel
            networkId={networkId}
            networkName={network?.Name}
            selectedId={selectedTgId}
            onSelect={(id) => setSelectedTgId((cur) => (cur === id ? null : id))}
            activePttGroupId={activePttGroupId}
            mutedGroupIds={mutedGroupIds}
            pinnedGroupIds={pinnedGroupIds}
            onPttStart={startPtt}
            onPttStop={stopPtt}
          />
        </div>

        {/* TOP-RIGHT — Live Map (with GPS drawer + subscriber detail
            flyout layered on top). Order matters: SubscriberDetail
            sits above the GPS drawer so the flyout still wins when
            both are open. */}
        <div style={{ minHeight: 0, position: "relative" }}>
          <MapPanel
            networkId={networkId}
            focusDeviceId={focusedDeviceId}
            focusBump={focusBump}
            freshPingDeviceIds={freshPingIds}
            pingCount={gpsPings.length}
            gpsPings={gpsPings}
            hasFreshPings={freshPingIds.size > 0}
            onTogglePings={() => setGpsDrawerOpen((v) => !v)}
          />
          <GpsPingsDrawer
            pings={gpsPings}
            open={gpsDrawerOpen}
            onClose={() => setGpsDrawerOpen(false)}
            onLocate={focusOnDevice}
          />
          <SubscriberDetail
            networkId={networkId}
            selectedId={selectedSubId}
            onClose={() => setSelectedSubId(null)}
            onLocate={focusOnDevice}
          />
        </div>

        {/* BOTTOM — Event Log spans cols 2+3 */}
        <div style={{ gridColumn: "2 / span 2", minHeight: 0 }}>
          <EventLogPanel />
        </div>
      </div>

      <StatusBar />

      <DiagnosticsPanel
        open={diagOpen}
        onClose={() => setDiagOpen(false)}
        networkId={networkId}
        gpsPingCount={gpsPings.length}
      />
    </div>
  );
}
